import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMaps } from "../../../config/google-maps-loader";
import { MapPin, Filter, Clock, Play, Square, Bell } from "lucide-react";
import TaskCard from "./task-card";
import ActiveJobTracking from "./active-job-tracking";
import toast from "react-hot-toast";
import { useSocket } from "../../../context/socket-context";

const Tasks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [latitude, setLatitude] = useState(31.4782);
  const [longitude, setLongitude] = useState(74.3140);
  const [address, setAddress] = useState("Loading location...");
  const [shiftActive, setShiftActive] = useState(() => {
    try {
      return localStorage.getItem("handymanShiftActive") === "true";
    } catch {
      return false;
    }
  });
  const [shiftStartTime, setShiftStartTime] = useState(() => {
    try {
      const saved = localStorage.getItem("handymanShiftStartTime");
      return saved ? new Date(saved) : null;
    } catch {
      return null;
    }
  });
  const [showShiftMessage, setShowShiftMessage] = useState(() => {
    try {
      return localStorage.getItem("handymanShiftActive") !== "true";
    } catch {
      return true;
    }
  });
  const [shiftLoading, setShiftLoading] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const taskMarkersRef = useRef({});
  const pendingTimestamps = useRef(new Map()); // Track when offers were sent for generic timeout
  const ignoredTaskIdsRef = useRef(
    new Set(
      (() => {
        try {
          return JSON.parse(localStorage.getItem("ignoredTaskIds") || "[]");
        } catch {
          return [];
        }
      })()
    )
  );

  const ignoreTaskId = (taskId) => {
    if (!taskId) return;
    ignoredTaskIdsRef.current.add(taskId);
    try {
      localStorage.setItem(
        "ignoredTaskIds",
        JSON.stringify(Array.from(ignoredTaskIdsRef.current))
      );
    } catch {
      // ignore storage failures
    }
  };

  const refreshCurrentLocation = async () => {
    if (!navigator.geolocation) {
      throw new Error("Geolocation not supported");
    }

    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
    });

    const { latitude: lat, longitude: lng } = pos.coords;
    setLatitude(lat);
    setLongitude(lng);

    let resolvedAddress = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      if (data?.display_name) resolvedAddress = data.display_name;
    } catch {
      // Fall back to coordinates
    }

    setAddress(resolvedAddress);
    return { lat, lng, address: resolvedAddress };
  };

  const { isLoaded } = useGoogleMaps();

  const [tasks, setTasks] = useState([]);
  const [visibleCards, setVisibleCards] = useState([]);
  const [acceptingOffers, setAcceptingOffers] = useState({});
  const [pendingOffers, setPendingOffers] = useState(new Set()); // Track pending offers

  // Auto-clear pending offers after 5 minutes to allow resubmission
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let changed = false;

      // Check existing pending offers
      // We need to use functional update to access latest state if we were setting standard state,
      // but here we are using a persistent ref source of truth + state sync.

      const idsToRemove = [];
      pendingTimestamps.current.forEach((time, id) => {
        // 5 minutes = 300,000 ms
        if (now - time > 300000) {
          idsToRemove.push(id);
          pendingTimestamps.current.delete(id);
        }
      });

      if (idsToRemove.length > 0) {
        setPendingOffers(prev => {
          const newSet = new Set(prev);
          let hasChanges = false;
          idsToRemove.forEach(id => {
            if (newSet.has(id)) {
              newSet.delete(id);
              hasChanges = true;
            }
          });
          return hasChanges ? newSet : prev;
        });

        idsToRemove.forEach(id => {
          updateTaskMarkerStatus(id, false);
          // Optional: Notify only once
          // Notify
          toast("Offer timed out. You can resubmit.", { id: `timeout-${id}`, icon: '⏳' });
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const authUser = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : null;
  const userId = authUser?._id;
  const token = localStorage.getItem("authToken");

  const {
    socket,
    isConnected,
    connectionStatus,
    newServiceRequests,
    setNewServiceRequests,
    acceptServiceRequest,
    activeJob,
    setActiveJob
  } = useSocket();

  useEffect(() => {
    const navJob = location?.state?.activeJob;
    if (navJob) {
      setActiveJob(navJob);
      navigate("/handyman/tasks", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  const addTaskMarker = (task) => {
    if (!isLoaded || !mapRef.current || !window.google?.maps || !task.coordinates) return null;

    const taskId = task.serviceRequestId;

    if (taskId && taskMarkersRef.current[taskId]) {
      const { marker, infoWindow } = taskMarkersRef.current[taskId];

      const position = {
        lat: task.coordinates.lat || task.coordinates[0],
        lng: task.coordinates.lng || task.coordinates[1]
      };

      marker.setPosition(position);
      marker.setTitle(task.jobTitle);
      marker.setIcon({
        url: pendingOffers.has(taskId)
          ? "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
          : "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
        scaledSize: new window.google.maps.Size(32, 32)
      });

      if (infoWindow) {
        infoWindow.setContent(`
          <div class="p-2 min-w-[200px]">
            <h3 class="font-bold text-gray-900 text-sm">${task.jobTitle}</h3>
            <p class="text-xs text-gray-600 mt-1">${task.subcategory || 'Service'}</p>
            <p class="text-xs text-gray-600">PKR ${task.basePrice}</p>
            <p class="text-xs text-gray-500 mt-2">${task.address || 'Location not specified'}</p>
            ${pendingOffers.has(taskId)
            ? '<p class="text-xs text-yellow-600 mt-1 font-semibold">⏳ Offer Pending</p>'
            : ''}
            ${task.expiryTime ? `<p class="text-xs text-red-500 mt-1">Expires: ${new Date(task.expiryTime).toLocaleTimeString()}</p>` : ''}
          </div>
        `);
      }

      return marker;
    }

    const position = {
      lat: task.coordinates.lat || task.coordinates[0],
      lng: task.coordinates.lng || task.coordinates[1]
    };

    const marker = new window.google.maps.Marker({
      position: position,
      map: mapRef.current,
      title: task.jobTitle,
      icon: {
        url: pendingOffers.has(taskId)
          ? "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
          : "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
        scaledSize: new window.google.maps.Size(32, 32)
      },
      animation: window.google.maps.Animation.DROP
    });

    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div class="p-2 min-w-[200px]">
          <h3 class="font-bold text-gray-900 text-sm">${task.jobTitle}</h3>
          <p class="text-xs text-gray-600 mt-1">${task.subcategory || 'Service'}</p>
          <p class="text-xs text-gray-600">PKR ${task.basePrice}</p>
          <p class="text-xs text-gray-500 mt-2">${task.address || 'Location not specified'}</p>
          ${pendingOffers.has(taskId)
          ? '<p class="text-xs text-yellow-600 mt-1 font-semibold">⏳ Offer Pending</p>'
          : ''}
          ${task.expiryTime ? `<p class="text-xs text-red-500 mt-1">Expires: ${new Date(task.expiryTime).toLocaleTimeString()}</p>` : ''}
        </div>
      `
    });

    marker.addListener("click", () => {
      infoWindow.open(mapRef.current, marker);
    });

    taskMarkersRef.current[taskId] = { marker, infoWindow };

    return marker;
  };

  const removeTaskMarker = (taskId) => {
    if (taskMarkersRef.current[taskId]) {
      const { marker, infoWindow } = taskMarkersRef.current[taskId];

      if (infoWindow) {
        infoWindow.close();
      }

      marker.setMap(null);
      delete taskMarkersRef.current[taskId];
    }
  };

  const clearAllTaskMarkers = () => {
    Object.keys(taskMarkersRef.current).forEach(taskId => {
      removeTaskMarker(taskId);
    });
    taskMarkersRef.current = {};
  };

  const updateTaskMarkerStatus = (taskId, isPending = true) => {
    if (taskMarkersRef.current[taskId]) {
      const { marker } = taskMarkersRef.current[taskId];

      marker.setIcon({
        url: isPending
          ? "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
          : "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
        scaledSize: new window.google.maps.Size(32, 32)
      });
    }
  };

  const removeTaskFromUI = (taskId) => {
    setVisibleCards((prev) => prev.filter((id) => id !== taskId));
    setTasks((prev) => prev.filter((task) => task.serviceRequestId !== taskId));
    setPendingOffers(prev => {
      const newSet = new Set(prev);
      newSet.delete(taskId);
      return newSet;
    });
  };

  useEffect(() => {
    if (!userId || !token) {
      return;
    }

    if (newServiceRequests.length > 0) {
      newServiceRequests.forEach((requestData) => {
        const normalizedRequestId = requestData.serviceRequestId || requestData.requestId;

        if (requestData.serviceType === "schedule") {
          return;
        }

        if (!normalizedRequestId) {
          return;
        }

        if (ignoredTaskIdsRef.current.has(normalizedRequestId)) {
          return;
        }

        const newTask = {
          serviceRequestId: normalizedRequestId,
          customerId: requestData.customerId,
          customerName: requestData.customerName,
          customerPhone: requestData.customerPhone,
          customerRating: requestData.customerRating,
          customerRatingCount: requestData.customerRatingCount,
          basePrice: requestData.basePrice,
          jobTitle: requestData.mainService,
          subcategory: requestData.subServices?.join(", ") || "",
          address: requestData.address,
          description: requestData.description,
          coordinates: requestData.coordinates,
          images: requestData.image ? [requestData.image] : [],
          video: requestData.video,
          isScheduled: requestData.serviceType === "schedule",
          scheduleDate: requestData.scheduleDate || null,
          scheduleTime: requestData.scheduleTime || null,
          expiryTime: requestData.expiryTime,
          createdAt: requestData.createdAt,
          serviceType: requestData.serviceType === 'live' ? 'Now' : 'Schedule',
        };

        setTasks((prevTasks) => {
          const existingIndex = prevTasks.findIndex((t) => t.serviceRequestId === newTask.serviceRequestId);

          if (existingIndex !== -1) {
            const updatedTasks = [...prevTasks];
            updatedTasks[existingIndex] = newTask;
            return updatedTasks;
          }
          return [newTask, ...prevTasks];
        });

        if (taskMarkersRef.current[newTask.serviceRequestId]) {
          addTaskMarker(newTask);
        }

        if (shiftActive) {
          setTimeout(() => {
            setVisibleCards((prev) => {
              if (prev.includes(newTask.serviceRequestId)) return prev;
              return [...prev, newTask.serviceRequestId];
            });

            addTaskMarker(newTask);
          }, 500);

          toast.success(`New ${requestData.mainService} task: PKR ${requestData.basePrice}`, {
            duration: 4000,
            icon: "🔔",
          });
        }
      });
    }
  }, [newServiceRequests, shiftActive, userId, token]);

  useEffect(() => {
    if (socket) {
      // Listen for offer acceptance confirmation
      socket.on('offer:accepted', (data) => {
        console.log("✅ Offer accepted by handyman:", data);
        const { serviceRequestId } = data;

        // Mark task as pending
        setPendingOffers(prev => new Set(prev).add(serviceRequestId));
        pendingTimestamps.current.set(serviceRequestId, Date.now()); // Update timestamp
        updateTaskMarkerStatus(serviceRequestId, true);

        // DO NOT remove from visible cards - keep showing but change status
        toast.success("Offer submitted! Waiting for customer response.", {
          duration: 3000,
        });

        setAcceptingOffers((prev) => ({ ...prev, [serviceRequestId]: false }));
      });

      // Listen for offer rejection by customer
      socket.on('offer:rejected', (data) => {
        const { serviceRequestId } = data;

        // Remove from pending offers and restore to available
        setPendingOffers(prev => {
          const newSet = new Set(prev);
          newSet.delete(serviceRequestId);
          return newSet;
        });
        pendingTimestamps.current.delete(serviceRequestId); // Clear timestamp

        updateTaskMarkerStatus(serviceRequestId, false);

        toast("Your offer was not accepted. Task is still available.", {
          duration: 3000,
        });
      });

      // Listen for offer timeout (no response from customer)
      socket.on('offer:timeout', (data) => {
        const { serviceRequestId } = data;

        // Don't show timeout if we already have an active job (customer already accepted)
        if (activeJob && activeJob.serviceRequestId === serviceRequestId) {
          return;
        }

        // Remove from pending offers and restore to available
        setPendingOffers(prev => {
          const newSet = new Set(prev);
          newSet.delete(serviceRequestId);
          return newSet;
        });
        pendingTimestamps.current.delete(serviceRequestId);

        updateTaskMarkerStatus(serviceRequestId, false);

        toast("No response from customer. Task is available again.", {
          duration: 3000,
        });
      });

      // Listen for customer accepting offer (customer:accept-offer from backend)
      socket.on('customer:accept-offer', (data) => {
        console.log("✅ Customer accepted offer:", data);
        const { serviceRequestId } = data;
        ignoreTaskId(serviceRequestId);

        // Find the task to get full details
        const task = tasks.find(t => t.serviceRequestId === serviceRequestId);
        toast.success("Customer accepted your offer! Starting job... 🎉", {
          duration: 4000,
        });

        if (task) {
          setActiveJob({
            ...task,
            ...data,
            status: 'assigned',
            customerPhone: task.customerPhone || data.customerPhoneNumber,
            customerPhoneNumber: task.customerPhone || data.customerPhoneNumber,
          });
        }

        // Remove the task immediately from the "Available Tasks" UI
        removeTaskFromUI(serviceRequestId);
        removeTaskMarker(serviceRequestId);
        
        // Remove from SocketContext newServiceRequests to prevent re-adding
        setNewServiceRequests(prev => 
          prev.filter(req => 
            req.serviceRequestId !== serviceRequestId && req.requestId !== serviceRequestId
          )
        );
      });

      // Keep legacy listener for backward compatibility
      socket.on('request:accepted', (data) => {
        const { serviceRequestId } = data;

        ignoreTaskId(serviceRequestId);

        const task = tasks.find(t => t.serviceRequestId === serviceRequestId);

        toast.success("Customer accepted your offer! Starting job... 🎉", {
          duration: 4000,
        });

        if (task) {
          setActiveJob({
            ...task,
            ...data,
            status: 'assigned',
            customerPhone: task.customerPhone || data.customerPhoneNumber,
            customerPhoneNumber: task.customerPhone || data.customerPhoneNumber,
          });
        }

        removeTaskFromUI(serviceRequestId);
        removeTaskMarker(serviceRequestId);
        
        setNewServiceRequests(prev => 
          prev.filter(req => 
            req.serviceRequestId !== serviceRequestId && req.requestId !== serviceRequestId
          )
        );
      });

      // Listen for deal completion (final confirmation) - alternative event
      socket.on('deal:completed', (data) => {
        const { serviceRequestId } = data;

        ignoreTaskId(serviceRequestId);

        // Find the task to get full details
        const task = tasks.find(t => t.serviceRequestId === serviceRequestId);

        toast.success("Job assigned! Let's get started! 🎉", {
          duration: 3000,
        });

        if (task) {
          setActiveJob({
            ...task,
            ...data,
            status: 'assigned',
            customerPhone: task.customerPhone,
            customerPhoneNumber: task.customerPhone,
          });
        }

        // Remove the task immediately from the "Available Tasks" UI
        removeTaskFromUI(serviceRequestId);
        removeTaskMarker(serviceRequestId);
      });

      socket.on('job:status:update', (data) => {
        const taskId = data?.taskId || data?.serviceRequestId;
        const nextStatus = data?.status;
        const paymentReceived = Boolean(
          data?.paymentReceived?.confirmedByHandyman ||
          data?.paymentReceived === true ||
          data?.paymentReceived?.confirmed === true
        );

        if (!taskId) return;

        if (
          paymentReceived ||
          nextStatus === 'payment_received' ||
          nextStatus === 'completed' ||
          nextStatus === 'cancelled'
        ) {
          ignoreTaskId(taskId);
          removeTaskFromUI(taskId);
          removeTaskMarker(taskId);
        }
      });

      // Listen for request expiry
      socket.on('request:expired', (data) => {
        const { serviceRequestId } = data;

        removeTaskFromUI(serviceRequestId);
        removeTaskMarker(serviceRequestId);

        toast("A task has expired and was removed from the map.", {
          duration: 3000,
        });
      });

      // Listen for task completion by customer (remove completed task from UI)
      socket.on('taskCompleted', (data) => {
        const taskId = data.taskId || data.serviceRequestId;
        
        if (taskId) {
          // Remove completed task from UI
          removeTaskFromUI(taskId);
          removeTaskMarker(taskId);
          
          // Remove from SocketContext newServiceRequests to prevent re-adding
          setNewServiceRequests(prev => 
            prev.filter(req => 
              req.serviceRequestId !== taskId && req.requestId !== taskId
            )
          );
        }
      });

      socket.on('task:completed', (data) => {
        const taskId = data.taskId || data.serviceRequestId;
        
        if (taskId) {
          removeTaskFromUI(taskId);
          removeTaskMarker(taskId);
          
          // Remove from SocketContext newServiceRequests
          setNewServiceRequests(prev => 
            prev.filter(req => 
              req.serviceRequestId !== taskId && req.requestId !== taskId
            )
          );
        }
      });

      socket.on('job:completed', (data) => {
        const taskId = data.taskId || data.serviceRequestId;
        
        if (taskId) {
          removeTaskFromUI(taskId);
          removeTaskMarker(taskId);
          
          // Remove from SocketContext newServiceRequests
          setNewServiceRequests(prev => 
            prev.filter(req => 
              req.serviceRequestId !== taskId && req.requestId !== taskId
            )
          );
        }
      });

      // Listen for dispute created event
      socket.on('dispute:created', (data) => {
        const taskId = data.taskId || data.serviceRequestId;
        if (taskId) {
          ignoreTaskId(taskId);
          removeTaskFromUI(taskId);
          removeTaskMarker(taskId);
          setNewServiceRequests(prev => 
            prev.filter(req => 
              req.serviceRequestId !== taskId && req.requestId !== taskId
            )
          );
        }
      });

      // Cleanup socket listeners
      return () => {
        socket.off('offer:accepted');
        socket.off('offer:rejected');
        socket.off('offer:timeout');
        socket.off('customer:accept-offer');
        socket.off('request:accepted');
        socket.off('deal:completed');
        socket.off('job:status:update');
        socket.off('request:expired');
        socket.off('taskCompleted');
        socket.off('task:completed');
        socket.off('job:completed');
        socket.off('dispute:created');
      };
    }
  }, [socket, tasks, navigate]);

  const handleAccept = async (taskId) => {
    setAcceptingOffers((prev) => ({ ...prev, [taskId]: true }));
    try {
      const task = tasks.find(t => t.serviceRequestId === taskId);
      if (!task) {
        throw new Error("Task not found");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/handyman/offers/accept`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            serviceRequestId: taskId,
            message: "I accept your offer at the requested price",
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to accept offer");
      }

      setPendingOffers(prev => new Set(prev).add(taskId));
      pendingTimestamps.current.set(taskId, Date.now());
      updateTaskMarkerStatus(taskId, true);

      // Use socket context for real-time updates if available
      if (acceptServiceRequest) {
        acceptServiceRequest(taskId);
      }

      // Show success message - task stays visible but marked as pending
      toast.success("Offer submitted! Waiting for customer response.", {
        duration: 3000,
      });

    } catch (error) {
      console.error("❌ Error accepting offer:", error);
      toast.error(error.message || "Failed to accept offer");
    } finally {
      setAcceptingOffers((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handleSendCounter = async (taskId, proposedPrice) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/handyman/offers/counter`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            serviceRequestId: taskId,
            proposedPrice: proposedPrice,
            message: `Counter offer: PKR ${proposedPrice}`,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to send counter offer");
      }

      // Mark as pending
      setPendingOffers(prev => new Set(prev).add(taskId));
      pendingTimestamps.current.set(taskId, Date.now());
      updateTaskMarkerStatus(taskId, true);

      toast.success("Counter offer sent! Waiting for customer response.", {
        duration: 3000,
      });

    } catch (error) {
      console.error("Error sending counter offer:", error);
      toast.error(error.message || "Failed to send counter offer");
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          setLatitude(lat);
          setLongitude(lng);

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            if (data?.display_name) setAddress(data.display_name);
          } catch {
            setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          }
        },
        (err) => {
          console.error("Geolocation Error:", err);
          setAddress("Unable to get location");
          toast.error("Location access denied or timed out. Please enable location services.");
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // 15 seconds timeout
          maximumAge: 0
        }
      );
    }
  }, []);

  const handleToggleShift = async () => {
    if (shiftActive) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/handyman/shift/end`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              user_id: userId,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok || data.success === false) {
          throw new Error(data.message || "Failed to end shift");
        }

        if (socket) {
          socket.emit('handyman:offline', { userId });
        }

        setShiftActive(false);
        setShiftStartTime(null);
        setShowShiftMessage(true);
        setVisibleCards([]);
        setTasks([]);
        setPendingOffers(new Set());
        clearAllTaskMarkers();
        
        // Persist shift state to localStorage
        try {
          localStorage.setItem("handymanShiftActive", "false");
          localStorage.removeItem("handymanShiftStartTime");
        } catch (error) {
          console.error("Failed to persist shift state:", error);
        }
        
        toast.success("Shift ended successfully");
      } catch (error) {
        console.error("Error ending shift:", error);
        toast.error(error.message || "Failed to end shift");
      }
    } else {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const permission = await navigator.permissions.query({
            name: "geolocation",
          });

          if (permission.state === "denied") {
            toast.error(
              "Location permission is denied. Please enable it in browser settings.",
              { duration: 5000 }
            );
            return;
          } else if (permission.state === "prompt") {
            try {
              const loc = await refreshCurrentLocation();
              await activateShift(loc);
            } catch (err) {
              console.error("Location access denied:", err);
              toast.error(err?.message || "Location access is required to start shift");
            }
            return;
          } else if (permission.state === "granted") {
            const loc = await refreshCurrentLocation();
            await activateShift(loc);
          }
        } else {
          try {
            const loc = await refreshCurrentLocation();
            await activateShift(loc);
          } catch (err) {
            console.error("Location access failed:", err);
            toast.error(err?.message || "Location access is required to start shift");
          }
        }
      } catch (error) {
        console.error("Permission check error:", error);
        try {
          const loc = await refreshCurrentLocation();
          await activateShift(loc);
        } catch (err) {
          toast.error(err?.message || "Location access is required to start shift");
        }
      }
    }
  };

  const activateShift = async (loc) => {
    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    setShiftLoading(true);

    try {
      const latToSend = loc?.lat ?? latitude;
      const lngToSend = loc?.lng ?? longitude;
      const addressToSend = loc?.address ?? address;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/handyman/shift/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            lat: latToSend,
            lng: lngToSend,
            address: addressToSend,
            user_id: userId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to start shift");
      }

      if (socket) {
        socket.emit('handyman:online', {
          userId,
          location: { lat: latToSend, lng: lngToSend, address: addressToSend }
        });
      }

      const startTime = new Date();
      setShiftActive(true);
      setShiftStartTime(startTime);
      setShowShiftMessage(false);
      
      // Persist shift state to localStorage
      try {
        localStorage.setItem("handymanShiftActive", "true");
        localStorage.setItem("handymanShiftStartTime", startTime.toISOString());
      } catch (error) {
        console.error("Failed to persist shift state:", error);
      }
      
      toast.success("Shift started! Listening for nearby tasks...");
    } catch (error) {
      console.error("Error starting shift:", error);
      toast.error(error.message || "Failed to start shift");
    } finally {
      setShiftLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google?.maps) return;

    const map = mapRef.current;

    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    const position = { lat: latitude, lng: longitude };
    markerRef.current = new window.google.maps.Marker({
      position: position,
      map: map,
      title: address,
      icon: {
        url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
      },
    });

    const infoWindow = new window.google.maps.InfoWindow({
      content: `<div class="p-2"><p class="font-semibold text-gray-900">Your Location</p><p class="text-sm text-gray-600">${address}</p></div>`,
    });

    markerRef.current.addListener("click", () => {
      infoWindow.open(map, markerRef.current);
    });

    map.setCenter(position);
    map.setZoom(13);

    if (shiftActive) {
      map.setOptions({
        draggable: true,
        zoomControl: true,
        scrollwheel: true,
        disableDoubleClickZoom: false,
      });
    } else {
      map.setOptions({
        draggable: false,
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
      });
    }
  }, [isLoaded, latitude, longitude, address, shiftActive]);

  useEffect(() => {
    return () => {
      clearAllTaskMarkers();
    };
  }, []);

  // Effect to clean up pending offers that might have expired silently (after 5 minutes)
  // This allows the handyman to resubmit an offer if they didn't get a response
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
  
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);



  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600 bg-green-50 border-green-200';
      case 'disconnected': return 'text-red-600 bg-red-50 border-red-200';
      case 'error': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // If there's an active job (customer accepted our offer), show the job tracking screen
  if (activeJob) {
    return (
      <ActiveJobTracking
        job={activeJob}
        onBack={() => setActiveJob(null)}
        onJobComplete={() => {
          setActiveJob(null);
          toast.success("Job completed! Great work! 🎉");
          // Stay on Tasks screen to see other available requests
        }}
      />
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] w-full flex flex-col bg-gray-50 dark:bg-slate-900">
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm dark:bg-slate-900 dark:border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Available Tasks</h1>
            <p className="text-gray-600 mt-1 flex items-center gap-2 dark:text-gray-300">
              <MapPin className="w-4 h-4 text-[#EFA938]" />
              {address}
            </p>
            <div className="flex items-center gap-4 mt-2">
              <div className={`px-3 py-1 rounded-full border text-xs font-medium flex items-center gap-2 ${getConnectionStatusColor()}`}>
                <Bell className="w-3 h-3" />
                {connectionStatus.toUpperCase()}
              </div>
              {tasks.length > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {tasks.length} task{tasks.length !== 1 ? 's' : ''} available
                  {pendingOffers.size > 0 && ` (${pendingOffers.size} pending)`}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleToggleShift}
              disabled={shiftLoading || !isConnected}
              className={`px-6 py-2 rounded-lg text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${shiftActive
                ? "bg-red-500 hover:bg-red-600"
                : "bg-[#EFA938] hover:bg-[#d78c1f]"
                }`}
            >
              {shiftLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              ) : shiftActive ? (
                <>
                  <Square className="w-4 h-4" />
                  End Shift
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Start Shift
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 relative bg-gray-100 overflow-hidden z-0">
        {!isLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/40 backdrop-blur-sm z-50">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
              <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">
                Loading map...
              </p>
            </div>
          </div>
        ) : (
          <GoogleMap
            onLoad={(map) => (mapRef.current = map)}
            center={{ lat: latitude, lng: longitude }}
            zoom={13}
            mapContainerStyle={{ width: "100%", height: "100%", minHeight: "400px" }}
            options={{
              mapId: "TASKS_MAP",
              disableDefaultUI: !shiftActive,
              zoomControl: shiftActive,
              scrollwheel: shiftActive,
              draggable: shiftActive,
            }}
          />
        )}

        <div className="absolute inset-0 flex flex-col items-center justify-start gap-4 z-50 pointer-events-none overflow-y-auto py-4">
          {visibleCards.map((taskId) => {
            const task = tasks.find((t) => t.serviceRequestId === taskId);
            if (!task) return null;

            const isPending = pendingOffers.has(taskId);

            return (
              <div
                key={taskId}
                className="transform transition-all duration-500 pointer-events-auto opacity-100 flex-shrink-0"
                style={{ width: "33%" }}
              >
                <TaskCard
                  task={task}
                  onAccept={handleAccept}
                  onSendCounter={handleSendCounter}
                  isAccepting={acceptingOffers[taskId]}
                  isPending={isPending}
                />
              </div>
            );
          })}
        </div>

        {!shiftActive && showShiftMessage && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center dark:bg-slate-800 dark:text-gray-100">
              <div className="mb-4 flex justify-center">
                <div className="bg-orange-100 rounded-full p-4 dark:bg-orange-900/30">
                  <Clock className="w-12 h-12 text-[#EFA938]" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Start Your Shift
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Click "Start Shift" to begin accepting tasks and viewing available requests in your area.
              </p>
              <button
                onClick={handleToggleShift}
                disabled={shiftLoading || !isConnected}
                className="w-full px-6 py-3 bg-[#EFA938] text-white font-semibold rounded-lg hover:bg-[#d78c1f] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {shiftLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Shift Now
                  </>
                )}
              </button>
              {!isConnected && (
                <p className="text-xs text-red-500 mt-3">
                  Cannot start shift: Socket connection required
                </p>
              )}
            </div>
          </div>
        )}

       
      </div>
    </div>
  );
};

export default Tasks;